"use client";

import { motion, useReducedMotion } from "framer-motion";
import {
  FaCircleHalfStroke,
  FaFileLines,
  FaHeading,
  FaImage,
  FaKeyboard,
  FaLink,
  FaListCheck,
  FaMobileScreen,
} from "react-icons/fa6";

const ease = [0.17, 0.85, 0.438, 0.99] as const;

const checkItems = [
  {
    title: "Images & Alt Text",
    description: "Checks for missing or unhelpful image descriptions.",
    icon: FaImage,
    color: "bg-blue-50 text-blue-600 dark:bg-blue-950/60 dark:text-blue-300",
  },
  {
    title: "Colour Contrast",
    description: "Makes sure text is readable with enough contrast.",
    icon: FaCircleHalfStroke,
    color:
      "bg-emerald-50 text-emerald-600 dark:bg-emerald-950/60 dark:text-emerald-300",
  },
  {
    title: "Headings & Structure",
    description: "Logical heading order that screen readers can follow.",
    icon: FaHeading,
    color:
      "bg-purple-50 text-purple-600 dark:bg-purple-950/60 dark:text-purple-300",
  },
  {
    title: "Forms & Labels",
    description: "Every form field is clearly and correctly labelled.",
    icon: FaListCheck,
    color:
      "bg-slate-100 text-slate-600 dark:bg-slate-800/80 dark:text-slate-300",
  },
  {
    title: "Keyboard Navigation",
    description: "Every feature works fully with a keyboard alone.",
    icon: FaKeyboard,
    color: "bg-pink-50 text-pink-600 dark:bg-pink-950/60 dark:text-pink-300",
  },
  {
    title: "Links & Buttons",
    description: "Clear, descriptive link and button text.",
    icon: FaLink,
    color:
      "bg-amber-50 text-amber-600 dark:bg-amber-950/60 dark:text-amber-300",
  },
  {
    title: "PDF & Documents",
    description: "Checks downloadable files are accessible too.",
    icon: FaFileLines,
    color: "bg-rose-50 text-rose-600 dark:bg-rose-950/60 dark:text-rose-300",
  },
  {
    title: "Mobile Accessibility",
    description: "A smooth, accessible experience on every device.",
    icon: FaMobileScreen,
    color: "bg-sky-50 text-sky-600 dark:bg-sky-950/60 dark:text-sky-300",
  },
];

export default function AdaComplianceWhatWeCheck() {
  const reduceMotion = useReducedMotion();
  const reveal = {
    initial: reduceMotion ? false : { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.15 },
    transition: { duration: reduceMotion ? 0 : 0.6, ease },
  };

  return (
    <motion.section
      {...reveal}
      className="bg-white px-4 py-14 sm:px-6 sm:py-20 lg:px-8 dark:bg-gray-950 transition-colors duration-300"
    >
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-10">
        <div className="mx-auto flex max-w-3xl flex-col items-center gap-3 text-center">
          <p className="font-poppins text-xs sm:text-sm font-bold uppercase tracking-widest text-pink-700 dark:text-pink-400">
            What We Check
          </p>
          <h2 className="font-poppins text-3xl font-bold text-indigo-950 dark:text-white sm:text-4xl">
            Comprehensive{" "}
            <span className="text-violet-700 dark:text-violet-400">
              Accessibility
            </span>{" "}
            Audit
          </h2>
          <p className="max-w-2xl text-sm sm:text-base leading-relaxed text-slate-600 dark:text-slate-300">
            We scan your website with industry-leading tools and add expert
            review based on WCAG 2.1 / 2.2 Level AA.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 sm:gap-5">
          {checkItems.map(({ title, description, icon: Icon, color }) => (
            <div
              key={title}
              className="flex items-start gap-4 rounded-2xl border border-slate-200/80 bg-white p-5 shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl dark:border-slate-800 dark:bg-slate-900"
            >
              <span
                className={`grid size-11 shrink-0 place-items-center rounded-xl ${color}`}
              >
                <Icon aria-hidden className="text-lg" />
              </span>
              <div className="min-w-0 flex-1">
                <h3 className="font-poppins text-sm font-bold text-indigo-950 dark:text-white sm:text-base">
                  {title}
                </h3>
                <p className="mt-1 text-xs leading-relaxed text-slate-600 dark:text-slate-300 sm:text-sm">
                  {description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </motion.section>
  );
}
